/**
 * fetchClient – axios replacement using the native Fetch API.
 * Reproduces the same response shape as axios:
 *   { data, headers, status }
 * and throws errors with the same shape:
 *   error.response = { data, status, headers }
 * so that errorHandler and all existing code keep working without any changes.
 */
import env from 'variables/config';

const BASE_URL: string = env.apiUrl;

export interface FetchClientResponse<T = any> {
    data: T;
    status: number;
    headers: Record<string, string>;
}

function headersToPlain(headers: Headers): Record<string, string> {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
        out[key] = value;
    });
    return out;
}

async function request<T = any>(
    method: string,
    url: string,
    options: {
        data?: any;
        headers?: Record<string, string>;
        responseType?: 'json' | 'blob' | 'arraybuffer' | 'text';
        params?: Record<string, string | number | boolean>;
    } = {},
): Promise<FetchClientResponse<T>> {
    // Build the full URL
    let fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;

    // Handle querystring params (like axios params option)
    if (options.params && Object.keys(options.params).length > 0) {
        const qs = new URLSearchParams();
        Object.entries(options.params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) qs.append(k, String(v));
        });
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs.toString();
    }

    const reqHeaders: Record<string, string> = { ...(options.headers ?? {}) };

    let body: BodyInit | undefined;
    if (options.data !== undefined) {
        if (options.data instanceof FormData) {
            // Let the browser set the Content-Type header with the boundary
            body = options.data;
        } else {
            reqHeaders['Content-Type'] = 'application/json';
            body = JSON.stringify(options.data);
        }
    }

    const response = await fetch(fullUrl, {
        method,
        headers: reqHeaders,
        body,
    });

    const plainHeaders = headersToPlain(response.headers);

    if (!response.ok) {
        // Reproduce axios error shape: err.response.data
        let errorData: any;
        if (
            options.responseType === 'blob' ||
            options.responseType === 'arraybuffer'
        ) {
            // For binary responses, decode the error as text
            try {
                const text = await response.text();
                errorData = text ? JSON.parse(text) : { message: response.statusText };
            } catch {
                errorData = { message: response.statusText };
            }
        } else {
            try {
                errorData = await response.json();
            } catch {
                errorData = { message: response.statusText };
            }
        }
        const err: any = new Error(
            errorData?.message || errorData?.error || 'Request failed',
        );
        err.response = {
            data: errorData,
            status: response.status,
            headers: plainHeaders,
        };
        throw err;
    }

    // Read the response body according to the requested responseType
    let data: any;
    if (options.responseType === 'blob') {
        data = await response.blob();
    } else if (options.responseType === 'arraybuffer') {
        data = await response.arrayBuffer();
    } else {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
    }

    return { data, status: response.status, headers: plainHeaders };
}

const fetchClient = {
    get<T = any>(url: string, config: any = {}): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        return request<T>('GET', url, { headers, responseType, params });
    },

    post<T = any>(
        url: string,
        data?: any,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        // Edge case: axios.post(url, config) called without a body (supplier.tsx)
        const body = data !== undefined ? data : config?.data;
        return request<T>('POST', url, { data: body, headers, responseType, params });
    },

    put<T = any>(
        url: string,
        data?: any,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        return request<T>('PUT', url, { data, headers, responseType, params });
    },

    patch<T = any>(
        url: string,
        data?: any,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        return request<T>('PATCH', url, { data, headers, responseType, params });
    },

    /**
     * DELETE – axios.delete(url, config) passes the body through config.data
     */
    delete<T = any>(
        url: string,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params, data } = config;
        return request<T>('DELETE', url, { data, headers, responseType, params });
    },
};

export default fetchClient;
